import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { ImageProcessingConfiguration } from '@babylonjs/core/Materials/imageProcessingConfiguration';
import { ColorCurves } from '@babylonjs/core/Materials/colorCurves';
import { Scene } from '@babylonjs/core/scene';
import type { Camera } from '@babylonjs/core/Cameras/camera';
import { duskSkyTexture } from './textures.js';

/**
 * Muted, conceptual lighting + gradient sky for the above-ground map (the subte
 * reads as dark naturally — enclosed, lit by emissive ceiling fixtures). The
 * look is a desaturated overcast/dusk wash (reference concept art): flat,
 * neutral-cool light, a pale hazy sky, heavier atmospheric fog, and a global
 * desaturation curve so nothing reads as candy-bright. No dynamic shadow maps
 * (perf) — the directional contrast + tone do the work.
 */
export function setupLighting(scene: Scene, camera: Camera): void {
  // Cool grey-lavender haze (the palette sky tone) — distance fades to a pale
  // overcast, never blue/golden/mauve.
  scene.clearColor = new Color4(0.56, 0.57, 0.63, 1);
  scene.fogMode = Scene.FOGMODE_EXP2;
  scene.fogColor = new Color3(0.72, 0.73, 0.8);
  scene.fogDensity = 0.0021; // moderate haze for depth, not a white-out

  // Gradient sky dome that tracks the camera (renders at infinity).
  const sky = MeshBuilder.CreateSphere('sky', { diameter: 1000, segments: 12, sideOrientation: 1 }, scene);
  const skyMat = new StandardMaterial('sky-mat', scene);
  skyMat.backFaceCulling = false;
  skyMat.disableLighting = true;
  skyMat.emissiveTexture = duskSkyTexture(scene);
  skyMat.diffuseColor = new Color3(0, 0, 0);
  sky.material = skyMat;
  sky.infiniteDistance = true;
  sky.applyFog = false;
  sky.isPickable = false;

  // Flat neutral sky fill + slightly warm ground bounce — keeps it readable
  // without colour cast. Higher fill + softer sun = the flat conceptual look.
  const hemi = new HemisphericLight('hemi', new Vector3(0, 1, 0), scene);
  hemi.intensity = 1.25;
  hemi.diffuse = new Color3(0.88, 0.88, 0.93);
  hemi.groundColor = new Color3(0.55, 0.55, 0.6); // cool bounce — no warm cast

  // Neutral-cool sun, softened so facades read as flat planes (not lit warm).
  const sun = new DirectionalLight('sun', new Vector3(-0.5, -0.82, 0.32), scene);
  sun.intensity = 1.1;
  sun.diffuse = new Color3(0.95, 0.95, 0.99);
  sun.specular = new Color3(0.15, 0.15, 0.18);

  // Tone grade: ACES + gentle contrast for a crisp concept-art read. NO global
  // desaturation — the palette itself is already limited/low-sat, and pulling
  // it further toward grey is exactly the muddy "washed-out" look we're avoiding.
  // The lone warm accent must stay punchy.
  const ip = scene.imageProcessingConfiguration;
  ip.toneMappingEnabled = true;
  ip.toneMappingType = ImageProcessingConfiguration.TONEMAPPING_ACES;
  ip.exposure = 1.2;
  ip.contrast = 1.18;
  const curves = new ColorCurves();
  curves.globalSaturation = 0; // palette controls hue; no extra wash
  ip.colorCurvesEnabled = true;
  ip.colorCurves = curves;

  void camera;
}
